import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, uploadFile, ocrScanCase } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { formatCZK, formatDate } from "@/lib/format";
import { HandCoins, Camera } from "lucide-react";
import { useDocPicker } from "@/components/shared/DocPicker";
import { toast } from "sonner";

const STATUS_MSG = {
  vydano: "Nahraj účtenky, ať se záloha dá vyúčtovat.",
  doklady: "Doklady máš nahrané. Až budeš mít všechny, řekni na centrále.",
  kontrola: "Vyúčtování kontroluje asistentka.",
  vyporadani: "Stav se na centrále — vypořádáte to podpisem.",
};

// Karta „Moje zálohy" na Přehledu — dělník si sám nahraje účtenku ke své záloze.
// Bez otevřených případů vrací null (žádný prázdný pruh na Přehledu).
// variant="band" (246): panel v záložce Žádosti na úvodu dělníka na PC.
// 258: jedna částka (k vrácení) a součet nahraných účtenek — bez „dostal jsi / doložil jsi“
// a bez celkové částky v hlavičce, když je záloha jen jedna (stejné číslo dvakrát).
// Velké číslo jako hodiny na kartě Příchod; popisek nad ním říká, kterým směrem peníze jdou.
const diffParts = (c) => (c.diff < 0 ? ["K vrácení", formatCZK(-c.diff)] : c.diff > 0 ? ["Firma ti doplatí", formatCZK(c.diff)] : ["Doklady sedí", formatCZK(0)]);
const OPEN = new Set(["vydano", "doklady"]);
const hintText = (c) => (c.status === "vydano" && c.diff < 0 ? "Peníze vrať, nebo dohraj účtenky."
  : OPEN.has(c.status) && c.diff > 0 ? "Účtenky jsou vyšší než záloha. Rozdíl dostaneš při vyúčtování." : STATUS_MSG[c.status]);
// Součet v hlavičce jen při víc zálohách k vrácení — jinak by tam bylo stejné číslo jako u zálohy.
const returnTotal = (cases) => { const r = cases.filter((c) => c.diff < 0); return r.length > 1 ? r.reduce((s, c) => s - c.diff, 0) : 0; };

export default function MyAdvancesCard({ variant }) {
  const [d, setD] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const targetCase = useRef(null);
  // 184: jedno tlačítko „Nahrát účtenku" → vyfotit / galerie / soubor podle telefonu.
  const docPicker = useDocPicker({ onPick: (file) => onFile({ target: { files: [file] } }) });

  const load = useCallback(() => {
    api.get("/cashbox/my-cases").then((r) => setD(r.data)).catch(() => setD({ cases: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  // 246: „Nahrát účtenku“ z úvodu na PC — k první otevřené záloze
  useEffect(() => {
    const go = () => { const c = (d?.cases || []).find((x) => x.status === "vydano" || x.status === "doklady") || (d?.cases || [])[0]; if (c) { targetCase.current = c; docPicker.open("uctenka"); } };
    window.addEventListener("pp-advance-receipt", go);
    return () => window.removeEventListener("pp-advance-receipt", go);
  }, [d, docPicker]);

  if (!d || d.cases.length === 0) return null;

  const pick = (c) => { targetCase.current = c; docPicker.open("uctenka"); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    const c = targetCase.current;
    if (!file || !c) return;
    setBusyId(c.id);
    try {
      const url = await uploadFile(await compressImage(file));
      const ocr = await ocrScanCase(c.id, url);
      await api.post(`/cashbox/my-cases/${c.id}/receipt`, {
        file_url: url, supplier_name: ocr.supplier_name, date: (ocr.date || "").slice(0, 10),
        total: Number(ocr.total) || 0, vat_amount: Number(ocr.vat_amount) || 0,
        items: ocr.items || [], receipt_number: ocr.receipt_number || null, currency: ocr.currency || null,   // 386
      });
      toast.success("Účtenka nahrána.");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusyId(null); }
  };

  if (variant === "band") {
    return (
      <div className="pp-band-panel rounded-[18px] px-4 pt-3.5 pb-3 min-w-0" data-testid="band-advances">
        {docPicker.inputs}
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-bold truncate">Moje zálohy</h3>
          {returnTotal(d.cases) > 0 && <span className="text-[13px] font-bold tabular-nums whitespace-nowrap" data-testid="my-adv-held">k vrácení celkem {formatCZK(returnTotal(d.cases))}</span>}
        </div>
        {d.cases.map((c) => (
          // vlevo popis a tlačítko, vpravo velká částka; v úzkém panelu částka přeskočí pod text (pořád vpravo)
          <div key={c.id} className="py-3 border-b border-white/[.12] last:border-b-0 min-w-0" data-testid={`my-adv-case-${c.human_id}`}>
            <div className="text-[14.5px] font-bold truncate">{c.human_id} · {c.purpose}</div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1 basis-[180px]">
              <div className="text-[12.5px] text-white/75 tabular-nums" data-testid={`my-adv-docs-${c.human_id}`}>Nahrané účtenky {formatCZK(c.documented)}</div>
              <div className="text-[12.5px] text-white/85 mt-0.5" data-testid={`my-adv-msg-${c.human_id}`}>{hintText(c)}</div>
              {c.status !== "vyporadani" && (busyId === c.id
                ? <div className="text-[13px] font-bold mt-2.5" data-testid={`my-adv-busy-${c.human_id}`}>Čtu účtenku…</div>
                : <button type="button" onClick={() => pick(c)} disabled={!!busyId} data-testid={`my-adv-photo-${c.human_id}`}
                    className="mt-2.5 h-9 px-4 rounded-full bg-white text-[#a84d2b] text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60"><Camera size={15} /> Nahrát účtenku</button>)}
            </div>
            <div className="ml-auto text-right shrink-0">
              <div className="text-[13px] text-white/80" data-testid={`my-adv-label-${c.human_id}`}>{diffParts(c)[0]}</div>
              <div className="font-heading font-extrabold tracking-tight leading-none tabular-nums whitespace-nowrap text-[38px] xl:text-[44px] mt-1" data-testid={`my-adv-amount-${c.human_id}`}>{diffParts(c)[1]}</div>
            </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="glass rounded-[20px] p-4 sm:p-5" data-testid="my-advances-card">
      {docPicker.inputs}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h2 className="text-[17px] font-black tracking-tight flex items-center gap-2">
          <HandCoins size={17} className="text-brand" /> Moje zálohy
        </h2>
        <div className="flex-1" />
        {returnTotal(d.cases) > 0 && (
          <span className="text-sm font-bold text-brand tabular-nums whitespace-nowrap" data-testid="my-adv-held">
            K vrácení celkem {formatCZK(returnTotal(d.cases))}
          </span>
        )}
      </div>

      <div className="space-y-4">
        {d.cases.map((c) => {
          const busy = busyId === c.id;
          return (
            <div key={c.id} className="border border-border rounded-[16px] p-3.5 bg-card/60" data-testid={`my-adv-case-${c.human_id}`}>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-bold tabular-nums shrink-0">{c.human_id}</span>
                <span className="text-muted-foreground truncate">· {c.purpose}</span>
              </div>
              <div className="mt-2 text-[13px] text-muted-foreground" data-testid={`my-adv-label-${c.human_id}`}>{diffParts(c)[0]}</div>
              <div className={`mt-0.5 font-heading font-extrabold tracking-tight leading-none tabular-nums whitespace-nowrap text-[34px] sm:text-[38px] ${
                c.diff >= 0 ? "text-success" : ""}`} data-testid={`my-adv-amount-${c.human_id}`}>
                {diffParts(c)[1]}
              </div>
              <div className="mt-2 text-[13px] text-muted-foreground" data-testid={`my-adv-docs-${c.human_id}`}>
                Nahrané účtenky <b className="text-foreground tabular-nums">{formatCZK(c.documented)}</b>
              </div>

              {c.receipts.length > 0 && (
                <div className="mt-1 space-y-1">
                  {c.receipts.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-[13px]">
                      <span className="truncate flex-1 min-w-0">{r.supplier_name || "Účtenka"}</span>
                      <span className="text-muted-foreground shrink-0">{formatDate(r.date)}</span>
                      <span className="font-bold tabular-nums shrink-0">{formatCZK(r.total)}</span>
                    </div>
                  ))}
                </div>
              )}

              <p className={`text-[13px] mt-2 ${c.status === "vyporadani" ? "font-bold text-brand" : "text-muted-foreground"}`}
                data-testid={`my-adv-msg-${c.human_id}`}>
                {hintText(c)}
              </p>

              {c.status !== "vyporadani" && (
                busy ? (
                  <div className="flex items-center gap-3 mt-3" data-testid={`my-adv-busy-${c.human_id}`}>
                    <div className="w-6 h-6 rounded-full border-[3px] border-secondary border-t-brand animate-spin motion-reduce:animate-none" />
                    <span className="text-sm text-muted-foreground">Čtu účtenku…</span>
                  </div>
                ) : (
                  <div className="flex md:justify-start mt-3">
                    <Button onClick={() => pick(c)} disabled={!!busyId}
                      className="h-11 w-full md:w-auto font-bold" data-testid={`my-adv-photo-${c.human_id}`}>
                      <Camera size={15} /> Nahrát účtenku
                    </Button>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
